import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Throttle window: only record a given page once per N ms per session.
// At 1000 concurrent users, every-30s polling would mean 2000 inserts/min.
// Per-route logging with a 10-minute dedupe keeps the table writable under load.
const DEDUPE_WINDOW_MS = 10 * 60 * 1000;

const getSessionId = (): string => {
  let id = sessionStorage.getItem('session_id');
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem('session_id', id);
  }
  return id;
};

const shouldTrack = (pathname: string): boolean => {
  try {
    const key = `activity_last_${pathname}`;
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last < DEDUPE_WINDOW_MS) return false;
    sessionStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return true;
  }
};

export const useUserActivity = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const pathname = window.location.pathname;
    if (!shouldTrack(pathname)) return;

    // Fire-and-forget; never block render or retry on failure
    supabase
      .from('user_actions')
      .insert({
        user_id: user.id,
        action: 'page_view',
        page_url: pathname,
        session_id: getSessionId(),
        details: { timestamp: new Date().toISOString() },
      })
      .then(({ error }) => {
        if (error) console.debug('activity track skipped:', error.message);
      });
    // Re-run when pathname changes (react-router updates trigger remount of consumers)
  }, [user, window.location.pathname]);
};
